import { useState, useEffect, useRef } from "react";

/* ─── DESIGN TOKENS ──────────────────────────────────────────────────── */
const T = {
  ink:           "#0A0A0A",
  steel:         "#2B5C8A",
  steelLt:       "#4A80B5",
  steelFaint:    "rgba(43,92,138,0.08)",
  surface:       "#F7F5F0",
  parchment:     "#EFECE5",
  border:        "rgba(26,26,26,0.13)",
  borderStrong:  "rgba(26,26,26,0.26)",
  red:           "#8B0000",
  gold:          "#7A5C1E",
  goldFaint:     "rgba(122,92,30,0.10)",
};

/* ─── ADMIN LOGIN MODAL ───────────────────────────────────────────────── */
const PORTALS = [
  {
    id: "family",
    label: "Family Office Portal",
    sub: "family.ashantitrust.org",
    desc: "Revocable Trust — Family Governance & Operations",
    href: "https://family.ashantitrust.org",
  },
  {
    id: "trust",
    label: "Irrevocable Trust Portal",
    sub: "trustmebro.ashantitrust.org",
    desc: "Dynasty / APT / Charitable Trust Administration",
    href: "https://trustmebro.ashantitrust.org",
  },
];

function AdminModal({ onClose }) {
  const [step, setStep]       = useState("select"); // select | login
  const [portal, setPortal]   = useState(null);
  const [email, setEmail]     = useState("");
  const [pass, setPass]       = useState("");
  const [err, setErr]         = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !pass) { setErr("Both fields are required."); return; }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setErr("Authentication service coming soon. Portal integration in progress.");
    }, 1200);
  };

  const overlay = {
    position: "fixed", inset: 0,
    background: "rgba(10,10,10,0.72)",
    backdropFilter: "blur(4px)",
    zIndex: 9000,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "20px",
  };
  const box = {
    background: T.surface,
    border: `1px solid ${T.borderStrong}`,
    borderTop: `3px solid ${T.steel}`,
    borderRadius: "3px",
    width: "100%", maxWidth: "480px",
    padding: "40px",
    position: "relative",
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        {/* Close */}
        <button onClick={onClose} style={{
          position:"absolute", top:"16px", right:"16px",
          background:"none", border:"none", cursor:"pointer",
          fontFamily:"DM Mono, monospace", fontSize:"18px", color:T.ink, opacity:0.5,
        }}>✕</button>

        {/* Header */}
        <div style={{ marginBottom:"28px" }}>
          <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.14em", color:T.steel, textTransform:"uppercase", marginBottom:"6px" }}>
            Secure Admin Access
          </div>
          <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:"26px", fontWeight:700, color:T.ink }}>
            {step === "select" ? "Select Portal" : portal.label}
          </div>
          {step === "login" && (
            <div style={{ fontFamily:"DM Mono, monospace", fontSize:"11px", color:T.gold, marginTop:"4px" }}>
              {portal.sub}
            </div>
          )}
        </div>

        {step === "select" ? (
          <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
            {PORTALS.map(p => (
              <button key={p.id} onClick={() => { setPortal(p); setStep("login"); }} style={{
                display:"block", textAlign:"left",
                background: T.steelFaint,
                border: `1px solid ${T.border}`,
                borderLeft: `3px solid ${T.steel}`,
                borderRadius:"3px",
                padding:"16px 20px",
                cursor:"pointer",
                transition:"background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(43,92,138,0.14)"}
              onMouseLeave={e => e.currentTarget.style.background = T.steelFaint}
              >
                <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"17px", color:T.ink, marginBottom:"3px" }}>
                  {p.label}
                </div>
                <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", color:T.steel, marginBottom:"4px" }}>{p.sub}</div>
                <div style={{ fontFamily:"EB Garamond, serif", fontSize:"14px", color:T.ink, opacity:0.65 }}>{p.desc}</div>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom:"14px" }}>
              <label style={labelSt}>Email Address</label>
              <input
                type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                style={inputSt}
                placeholder="admin@ashantitrust.org"
                autoComplete="username"
              />
            </div>
            <div style={{ marginBottom:"20px" }}>
              <label style={labelSt}>Password</label>
              <input
                type="password" value={pass}
                onChange={e => setPass(e.target.value)}
                style={inputSt}
                placeholder="••••••••••"
                autoComplete="current-password"
              />
            </div>
            {err && (
              <div style={{ fontFamily:"EB Garamond, serif", fontSize:"14px", color:T.red, marginBottom:"14px", padding:"10px 14px", background:"rgba(139,0,0,0.06)", borderLeft:`2px solid ${T.red}`, borderRadius:"2px" }}>
                {err}
              </div>
            )}
            <div style={{ display:"flex", gap:"10px" }}>
              <button type="button" onClick={() => { setStep("select"); setErr(""); }} style={{
                flex:1, height:"44px", background:"none",
                border:`1px solid ${T.borderStrong}`, borderRadius:"3px",
                fontFamily:"DM Mono, monospace", fontSize:"11px", letterSpacing:"0.08em",
                color:T.ink, cursor:"pointer",
              }}>← Back</button>
              <button type="submit" disabled={loading} style={{
                flex:2, height:"44px",
                background: loading ? T.steelFaint : T.steel,
                border:"none", borderRadius:"3px",
                fontFamily:"DM Mono, monospace", fontSize:"11px", letterSpacing:"0.08em",
                color:"#fff", cursor:"pointer",
                transition:"background 0.15s",
              }}>
                {loading ? "Authenticating…" : "Sign In →"}
              </button>
            </div>
            <div style={{ marginTop:"14px", textAlign:"center", fontFamily:"DM Mono, monospace", fontSize:"10px", color:T.ink, opacity:0.4 }}>
              Authorized personnel only · Activity is logged
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
const labelSt = { display:"block", fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.1em", textTransform:"uppercase", color:T.ink, opacity:0.6, marginBottom:"6px" };
const inputSt = { display:"block", width:"100%", height:"44px", padding:"0 14px", fontFamily:"EB Garamond, serif", fontSize:"16px", color:T.ink, background:"#fff", border:`1px solid ${T.borderStrong}`, borderRadius:"3px", outline:"none" };

/* ─── NAV ─────────────────────────────────────────────────────────────── */
const NAV_ITEMS = ["Learn", "Resources", "Events", "About", "Donate"];

function Nav({ activePage, setPage, openAdmin }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <nav style={{
      position:"fixed", top:0, left:0, right:0, zIndex:500,
      background: scrolled ? "rgba(247,245,240,0.97)" : "transparent",
      borderBottom: scrolled ? `1px solid ${T.border}` : "1px solid transparent",
      backdropFilter: scrolled ? "blur(8px)" : "none",
      transition:"all 0.3s ease",
      padding:"0 32px",
      height:"64px",
      display:"flex", alignItems:"center", justifyContent:"space-between",
    }}>
      {/* Logo */}
      <button onClick={() => setPage("home")} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:"10px" }}>
        <div style={{
          width:"32px", height:"32px",
          background: T.steel,
          borderRadius:"2px",
          display:"flex", alignItems:"center", justifyContent:"center",
          flexShrink:0,
        }}>
          <span style={{ fontFamily:"Bebas Neue, sans-serif", fontSize:"16px", color:"#fff", letterSpacing:"0.05em" }}>AT</span>
        </div>
        <div style={{ textAlign:"left" }}>
          <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"17px", color:T.ink, lineHeight:1 }}>AshantiTrust</div>
          <div style={{ fontFamily:"DM Mono, monospace", fontSize:"9px", color:T.steel, letterSpacing:"0.1em", textTransform:"uppercase", lineHeight:1.4 }}>501(c)(3) Education</div>
        </div>
      </button>

      {/* Desktop Nav */}
      <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
        {NAV_ITEMS.map(item => (
          <button key={item} onClick={() => setPage(item.toLowerCase())} style={{
            background:"none", border:"none", cursor:"pointer",
            fontFamily:"DM Mono, monospace", fontSize:"11px", letterSpacing:"0.1em",
            textTransform:"uppercase", color: activePage === item.toLowerCase() ? T.steel : T.ink,
            padding:"8px 14px", borderRadius:"2px",
            borderBottom: activePage === item.toLowerCase() ? `2px solid ${T.steel}` : "2px solid transparent",
            transition:"all 0.15s",
          }}>{item}</button>
        ))}
        <div style={{ width:"1px", height:"22px", background:T.border, margin:"0 8px" }} />
        <button onClick={openAdmin} style={{
          height:"36px", padding:"0 18px",
          background:T.steel, border:"none", borderRadius:"3px",
          fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.12em",
          textTransform:"uppercase", color:"#fff", cursor:"pointer",
        }}>Admin →</button>
      </div>
    </nav>
  );
}

/* ─── HERO ────────────────────────────────────────────────────────────── */
function Hero({ setPage }) {
  return (
    <section style={{
      minHeight:"100vh",
      background: T.parchment,
      display:"flex", flexDirection:"column", justifyContent:"center",
      padding:"120px 32px 80px",
      position:"relative",
      overflow:"hidden",
    }}>
      {/* Decorative background element */}
      <div style={{
        position:"absolute", top:"-80px", right:"-60px",
        width:"520px", height:"520px",
        background:`radial-gradient(circle, rgba(43,92,138,0.06) 0%, transparent 70%)`,
        borderRadius:"50%",
        pointerEvents:"none",
      }} />
      <div style={{
        position:"absolute", bottom:"60px", left:"-40px",
        width:"320px", height:"320px",
        background:`radial-gradient(circle, rgba(122,92,30,0.05) 0%, transparent 70%)`,
        borderRadius:"50%",
        pointerEvents:"none",
      }} />

      <div style={{ maxWidth:"880px", margin:"0 auto", width:"100%", position:"relative" }}>
        <div style={{ fontFamily:"DM Mono, monospace", fontSize:"11px", letterSpacing:"0.18em", textTransform:"uppercase", color:T.steel, marginBottom:"20px", display:"flex", alignItems:"center", gap:"10px" }}>
          <div style={{ width:"32px", height:"1px", background:T.steel }} />
          Public Trust Education · 501(c)(3)
        </div>

        <h1 style={{
          fontFamily:"Cormorant Garamond, serif", fontWeight:700,
          fontSize:"clamp(44px, 8vw, 96px)",
          lineHeight:1.0, color:T.ink, marginBottom:"28px",
          letterSpacing:"-0.02em",
        }}>
          Learn Trusts.<br />
          <span style={{ color:T.steel }}>Protect Wealth.</span><br />
          Build Legacy.
        </h1>

        <p style={{
          fontFamily:"EB Garamond, serif", fontSize:"clamp(17px, 2.2vw, 22px)",
          color:T.ink, opacity:0.75, maxWidth:"560px",
          lineHeight:1.6, marginBottom:"40px",
        }}>
          AshantiTrust.org is a nonprofit dedicated to educating families, entrepreneurs, and professionals on trust planning, estate law concepts, asset protection, and multi-generational wealth preservation.
        </p>

        <div style={{ display:"flex", gap:"14px", flexWrap:"wrap" }}>
          <button onClick={() => setPage("learn")} style={{
            height:"52px", padding:"0 32px",
            background:T.steel, border:"none", borderRadius:"3px",
            fontFamily:"DM Mono, monospace", fontSize:"12px", letterSpacing:"0.12em",
            textTransform:"uppercase", color:"#fff", cursor:"pointer",
          }}>
            Start Learning →
          </button>
          <button onClick={() => setPage("resources")} style={{
            height:"52px", padding:"0 32px",
            background:"none", border:`1px solid ${T.borderStrong}`, borderRadius:"3px",
            fontFamily:"DM Mono, monospace", fontSize:"12px", letterSpacing:"0.12em",
            textTransform:"uppercase", color:T.ink, cursor:"pointer",
          }}>
            Free Resources
          </button>
        </div>

        {/* Stats bar */}
        <div style={{
          display:"flex", gap:"0", marginTop:"72px",
          borderTop:`1px solid ${T.border}`,
          paddingTop:"32px",
        }}>
          {[
            { n:"200+", label:"Education Articles" },
            { n:"6",    label:"Learning Tracks" },
            { n:"50+",  label:"Free Templates" },
            { n:"501(c)(3)", label:"Nonprofit Status" },
          ].map((s, i) => (
            <div key={i} style={{
              flex:1, paddingRight:"24px",
              borderRight: i < 3 ? `1px solid ${T.border}` : "none",
              paddingLeft: i > 0 ? "24px" : 0,
            }}>
              <div style={{ fontFamily:"Bebas Neue, sans-serif", fontSize:"32px", color:T.steel, lineHeight:1 }}>{s.n}</div>
              <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.1em", textTransform:"uppercase", color:T.ink, opacity:0.55, marginTop:"4px" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── TRUST TYPES SECTION ─────────────────────────────────────────────── */
const TRUST_TYPES = [
  { name:"Revocable Living Trust", tag:"Foundational", color:T.steel, desc:"The cornerstone of most estate plans. Avoids probate, maintains control during life, and transfers seamlessly at death." },
  { name:"Irrevocable Trust", tag:"Asset Protection", color:T.gold, desc:"Removes assets from taxable estate. Provides creditor protection and multi-generational tax advantages." },
  { name:"Dynasty Trust", tag:"Multi-Generation", color:T.ink, desc:"Designed to hold wealth across multiple generations, minimizing estate and GST taxes over 100+ years." },
  { name:"Charitable Remainder Trust", tag:"Philanthropy", color:"#5A7A4A", desc:"Generate income for yourself or heirs while leaving the remainder to charity. Significant tax deduction." },
  { name:"Asset Protection Trust", tag:"Liability Shield", color:T.red, desc:"Protect assets from future creditors while retaining discretionary benefit access." },
  { name:"Special Needs Trust", tag:"Care Planning", color:"#5A4A7A", desc:"Preserve government benefit eligibility for a disabled beneficiary while providing supplemental support." },
];

function TrustTypes({ setPage }) {
  return (
    <section style={{ background:T.surface, padding:"80px 32px" }}>
      <div style={{ maxWidth:"1100px", margin:"0 auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:"40px", flexWrap:"wrap", gap:"16px" }}>
          <div>
            <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:T.steel, marginBottom:"8px" }}>Trust Education Center</div>
            <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"38px", color:T.ink }}>Core Trust Structures</h2>
          </div>
          <button onClick={() => setPage("learn")} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:"3px", height:"36px", padding:"0 18px", fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.1em", textTransform:"uppercase", color:T.steel, cursor:"pointer" }}>
            View All →
          </button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:"16px" }}>
          {TRUST_TYPES.map((t, i) => (
            <div key={i} style={{
              background:T.parchment,
              border:`1px solid ${T.border}`,
              borderTop:`3px solid ${t.color}`,
              borderRadius:"3px",
              padding:"24px",
              cursor:"pointer",
              transition:"transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,0.08)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="none"; }}
            >
              <div style={{ fontFamily:"DM Mono, monospace", fontSize:"9px", letterSpacing:"0.14em", textTransform:"uppercase", color:t.color, marginBottom:"8px" }}>{t.tag}</div>
              <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"20px", color:T.ink, marginBottom:"10px" }}>{t.name}</div>
              <div style={{ fontFamily:"EB Garamond, serif", fontSize:"15px", color:T.ink, opacity:0.7, lineHeight:1.55 }}>{t.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── LEARNING TRACKS ─────────────────────────────────────────────────── */
const TRACKS = [
  { title:"Trust Basics 101", lessons:"8 lessons", icon:"📖", desc:"Understand the foundational concepts every family should know before creating a trust." },
  { title:"Trustee Education", lessons:"12 lessons", icon:"⚖️", desc:"Fiduciary duties, investment oversight, distribution management, and annual compliance." },
  { title:"Tax-Advantaged Trusts", lessons:"10 lessons", icon:"📊", desc:"Income tax, estate tax, gift tax, GST, and grantor trust rule essentials." },
  { title:"Asset Protection Fundamentals", lessons:"7 lessons", icon:"🛡️", desc:"Creditor protection strategies, LLCs, insurance integration, and real estate risk." },
  { title:"Family Governance", lessons:"6 lessons", icon:"🏛️", desc:"Family constitutions, trustee succession, beneficiary education, and conflict resolution." },
  { title:"Legacy & Philanthropy", lessons:"9 lessons", icon:"🌿", desc:"Charitable trusts, private foundations, donor-advised funds, and 100-year planning." },
];

function LearningTracks({ setPage }) {
  return (
    <section style={{ background:T.parchment, padding:"80px 32px" }}>
      <div style={{ maxWidth:"1100px", margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:"48px" }}>
          <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:T.gold, marginBottom:"8px" }}>Learning Center</div>
          <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"38px", color:T.ink }}>Six Learning Tracks</h2>
          <p style={{ fontFamily:"EB Garamond, serif", fontSize:"18px", color:T.ink, opacity:0.65, marginTop:"12px", maxWidth:"500px", margin:"12px auto 0" }}>
            Structured education from trust basics to advanced multi-generational strategy.
          </p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:"2px", background:T.borderStrong, border:`1px solid ${T.borderStrong}`, borderRadius:"3px", overflow:"hidden" }}>
          {TRACKS.map((t, i) => (
            <div key={i} style={{
              background:T.surface, padding:"28px 24px",
              cursor:"pointer", transition:"background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = T.steelFaint}
            onMouseLeave={e => e.currentTarget.style.background = T.surface}
            onClick={() => setPage("learn")}
            >
              <div style={{ fontSize:"28px", marginBottom:"12px" }}>{t.icon}</div>
              <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"19px", color:T.ink, marginBottom:"4px" }}>{t.title}</div>
              <div style={{ fontFamily:"DM Mono, monospace", fontSize:"9px", color:T.steel, letterSpacing:"0.1em", marginBottom:"10px" }}>{t.lessons}</div>
              <div style={{ fontFamily:"EB Garamond, serif", fontSize:"14px", color:T.ink, opacity:0.65, lineHeight:1.55 }}>{t.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── RESOURCE PREVIEW ────────────────────────────────────────────────── */
const RESOURCES = [
  { type:"Checklist", title:"New Trust Planning Checklist", desc:"Everything you need before meeting with your estate attorney." },
  { type:"Template", title:"Family Constitution Template", desc:"Codify your family values, governance rules, and wealth philosophy." },
  { type:"Checklist", title:"Annual Trustee Review Checklist", desc:"12-point fiduciary compliance checklist for active trustees." },
  { type:"Template", title:"Asset Inventory Template", desc:"Complete registry of real estate, businesses, accounts, and insurance." },
  { type:"Tool", title:"Trust Type Comparison Tool", desc:"Interactive guide to matching trust structures to your planning goals." },
  { type:"Checklist", title:"Estate Planning Readiness Checklist", desc:"Assess your current documents against best-practice standards." },
];

const TYPE_COLORS = { Checklist: T.steel, Template: T.gold, Tool: T.red };

function ResourcePreview({ setPage }) {
  return (
    <section style={{ background:T.surface, padding:"80px 32px" }}>
      <div style={{ maxWidth:"1100px", margin:"0 auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:"40px", flexWrap:"wrap", gap:"16px" }}>
          <div>
            <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:T.gold, marginBottom:"8px" }}>Resource Library</div>
            <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"38px", color:T.ink }}>Free Downloads</h2>
          </div>
          <button onClick={() => setPage("resources")} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:"3px", height:"36px", padding:"0 18px", fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.1em", textTransform:"uppercase", color:T.steel, cursor:"pointer" }}>
            All Resources →
          </button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:"12px" }}>
          {RESOURCES.map((r, i) => (
            <div key={i} style={{
              background:T.parchment, border:`1px solid ${T.border}`,
              borderRadius:"3px", padding:"20px 22px",
              display:"flex", gap:"14px", alignItems:"flex-start",
              cursor:"pointer", transition:"border-color 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.steelLt}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
            >
              <div style={{ width:"3px", height:"100%", background:TYPE_COLORS[r.type] || T.steel, borderRadius:"2px", minHeight:"60px" }} />
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"DM Mono, monospace", fontSize:"9px", letterSpacing:"0.12em", textTransform:"uppercase", color:TYPE_COLORS[r.type], marginBottom:"5px" }}>{r.type}</div>
                <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"17px", color:T.ink, marginBottom:"6px" }}>{r.title}</div>
                <div style={{ fontFamily:"EB Garamond, serif", fontSize:"14px", color:T.ink, opacity:0.65, lineHeight:1.5 }}>{r.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── DISCLAIMER BANNER ───────────────────────────────────────────────── */
function Disclaimer() {
  return (
    <div style={{
      background:T.goldFaint,
      borderTop:`1px solid rgba(122,92,30,0.2)`,
      borderBottom:`1px solid rgba(122,92,30,0.2)`,
      padding:"16px 32px",
      textAlign:"center",
    }}>
      <span style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.08em", color:T.gold }}>
        ⚖️ Educational information only. Not legal, tax, investment, or fiduciary advice. Consult licensed counsel, CPA, and financial advisors for your situation.
      </span>
    </div>
  );
}

/* ─── DONATE SECTION ──────────────────────────────────────────────────── */
function DonateBanner({ setPage }) {
  return (
    <section style={{
      background:T.steel,
      padding:"64px 32px",
      position:"relative",
      overflow:"hidden",
    }}>
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 80% 50%, rgba(255,255,255,0.05) 0%, transparent 60%)", pointerEvents:"none" }} />
      <div style={{ maxWidth:"720px", margin:"0 auto", textAlign:"center", position:"relative" }}>
        <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:"rgba(255,255,255,0.6)", marginBottom:"10px" }}>Support the Mission</div>
        <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"38px", color:"#fff", marginBottom:"16px" }}>
          Help Families Build Generational Wealth
        </h2>
        <p style={{ fontFamily:"EB Garamond, serif", fontSize:"18px", color:"rgba(255,255,255,0.8)", lineHeight:1.6, marginBottom:"32px" }}>
          Your tax-deductible donation funds free trust education, downloadable resources, and financial literacy programs for underserved communities.
        </p>
        <div style={{ display:"flex", gap:"12px", justifyContent:"center", flexWrap:"wrap" }}>
          <button onClick={() => setPage("donate")} style={{
            height:"52px", padding:"0 36px",
            background:"#fff", border:"none", borderRadius:"3px",
            fontFamily:"DM Mono, monospace", fontSize:"12px", letterSpacing:"0.12em",
            textTransform:"uppercase", color:T.steel, cursor:"pointer",
          }}>Donate Now</button>
          <button onClick={() => setPage("about")} style={{
            height:"52px", padding:"0 28px",
            background:"none", border:"1px solid rgba(255,255,255,0.4)", borderRadius:"3px",
            fontFamily:"DM Mono, monospace", fontSize:"12px", letterSpacing:"0.12em",
            textTransform:"uppercase", color:"#fff", cursor:"pointer",
          }}>Our Mission</button>
        </div>
      </div>
    </section>
  );
}

/* ─── FULL PAGE SECTIONS ──────────────────────────────────────────────── */

function LearnPage() {
  const CATEGORIES = [
    { title:"Trust Fundamentals", items:["What is a trust?","Grantor, trustee, beneficiary roles","Revocable vs irrevocable trusts","Trust situs","Fiduciary duties","Trust funding basics"] },
    { title:"Estate Planning", items:["Wills vs trusts","Probate avoidance","Beneficiary designations","Powers of attorney","Healthcare directives","Letter of intent"] },
    { title:"Tax Planning", items:["Trust income tax basics","Form 1041 overview","Estate tax concepts","Gift tax concepts","GST tax","Grantor trust rules"] },
    { title:"Asset Protection", items:["Creditor protection concepts","LLCs and trusts","Insurance integration","Business liability","Real estate risk","Fraudulent transfer warnings"] },
    { title:"Family Governance", items:["Family constitution","Family meetings","Trustee succession","Beneficiary education","Decision rights","Conflict resolution"] },
    { title:"Charitable Planning", items:["Private foundations","Donor advised funds","Charitable remainder trusts","Charitable lead trusts","Scholarship funds","Legacy giving"] },
  ];
  return (
    <div style={{ maxWidth:"1100px", margin:"0 auto", padding:"100px 32px 80px" }}>
      <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:T.steel, marginBottom:"8px" }}>Trust Education Center</div>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"48px", color:T.ink, marginBottom:"12px" }}>Learn</h1>
      <p style={{ fontFamily:"EB Garamond, serif", fontSize:"18px", color:T.ink, opacity:0.65, marginBottom:"48px", maxWidth:"560px" }}>
        Structured knowledge for families, trustees, and advisors navigating trust planning, estate law, and multi-generational wealth.
      </p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:"24px" }}>
        {CATEGORIES.map((cat, i) => (
          <div key={i} style={{ background:T.surface, border:`1px solid ${T.border}`, borderTop:`3px solid ${T.steel}`, borderRadius:"3px", padding:"24px" }}>
            <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"20px", color:T.ink, marginBottom:"16px" }}>{cat.title}</div>
            <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
              {cat.items.map((item, j) => (
                <div key={j} style={{ display:"flex", alignItems:"center", gap:"10px", padding:"6px 0", borderBottom:`1px solid ${T.border}`, cursor:"pointer" }}>
                  <div style={{ width:"4px", height:"4px", borderRadius:"50%", background:T.steel, flexShrink:0 }} />
                  <span style={{ fontFamily:"EB Garamond, serif", fontSize:"15px", color:T.ink }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResourcesPage() {
  const [tab, setTab] = useState("checklists");
  const TABS = ["checklists","templates","tools"];
  const DATA = {
    checklists:[
      { title:"New Trust Planning Checklist", desc:"Everything you need before meeting with your estate attorney." },
      { title:"Trust Funding Checklist", desc:"Step-by-step guide to titling assets into your trust." },
      { title:"Annual Trustee Review Checklist", desc:"12-point fiduciary compliance checklist for active trustees." },
      { title:"Beneficiary Education Checklist", desc:"Prepare beneficiaries for future trustee and stewardship roles." },
      { title:"Estate Planning Readiness Checklist", desc:"Assess your documents against best-practice standards." },
      { title:"Charitable Giving Checklist", desc:"Annual planning checklist for tax-efficient charitable contributions." },
    ],
    templates:[
      { title:"Trustee Meeting Agenda", desc:"Structured agenda template for annual trust review meetings." },
      { title:"Family Constitution Template", desc:"Codify your family values, governance rules, and wealth philosophy." },
      { title:"Asset Inventory Template", desc:"Complete registry of real estate, businesses, accounts, and insurance." },
      { title:"Family Mission Statement Template", desc:"Define your family's purpose, values, and wealth philosophy." },
      { title:"Trust Review Minutes Template", desc:"Document trustee decisions and annual review outcomes." },
      { title:"Advisor Contact Sheet", desc:"Centralized contact registry for your full advisory team." },
    ],
    tools:[
      { title:"Trust Type Comparison Tool", desc:"Interactive guide to matching trust structures to planning goals." },
      { title:"Estate Planning Document Tracker", desc:"Track which documents you have and what still needs to be created." },
      { title:"Family Governance Scorecard", desc:"Self-assessment of your family's governance readiness." },
      { title:"Charitable Giving Planner", desc:"Plan and optimize your annual charitable contributions." },
      { title:"Trust Glossary", desc:"200+ terms defined in plain language for trustees and beneficiaries." },
    ],
  };
  return (
    <div style={{ maxWidth:"1000px", margin:"0 auto", padding:"100px 32px 80px" }}>
      <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:T.gold, marginBottom:"8px" }}>Resource Library</div>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"48px", color:T.ink, marginBottom:"32px" }}>Free Resources</h1>
      <div style={{ display:"flex", gap:"2px", marginBottom:"32px", background:T.border, padding:"2px", borderRadius:"3px", width:"fit-content" }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            height:"36px", padding:"0 20px",
            background: tab===t ? T.steel : "transparent",
            border:"none", borderRadius:"2px",
            fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.1em",
            textTransform:"uppercase", color: tab===t ? "#fff" : T.ink, cursor:"pointer",
          }}>{t}</button>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:"12px" }}>
        {DATA[tab].map((r, i) => (
          <div key={i} style={{
            background:T.parchment, border:`1px solid ${T.border}`,
            borderRadius:"3px", padding:"20px 22px",
            cursor:"pointer",
          }}>
            <div style={{ fontFamily:"DM Mono, monospace", fontSize:"9px", letterSpacing:"0.12em", textTransform:"uppercase", color:TYPE_COLORS[tab==="checklists"?"Checklist":tab==="templates"?"Template":"Tool"], marginBottom:"6px" }}>
              {tab==="checklists"?"Checklist":tab==="templates"?"Template":"Tool"} · Free
            </div>
            <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"18px", color:T.ink, marginBottom:"8px" }}>{r.title}</div>
            <div style={{ fontFamily:"EB Garamond, serif", fontSize:"14px", color:T.ink, opacity:0.65, lineHeight:1.5, marginBottom:"14px" }}>{r.desc}</div>
            <button style={{
              height:"32px", padding:"0 16px",
              background:T.steel, border:"none", borderRadius:"2px",
              fontFamily:"DM Mono, monospace", fontSize:"9px", letterSpacing:"0.1em",
              textTransform:"uppercase", color:"#fff", cursor:"pointer",
            }}>Download →</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AboutPage() {
  return (
    <div style={{ maxWidth:"880px", margin:"0 auto", padding:"100px 32px 80px" }}>
      <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:T.steel, marginBottom:"8px" }}>About</div>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"48px", color:T.ink, marginBottom:"28px" }}>Our Mission</h1>
      {[
        { heading:"Why We Exist", body:"Too many families—especially those first encountering significant wealth—make irreversible decisions without understanding the tools available to them. Trusts, estate documents, and tax-advantaged strategies remain locked behind expensive advisors and opaque legal language. AshantiTrust.org exists to change that." },
        { heading:"What We Do", body:"We produce rigorous, attorney-reviewed educational content on trust law, estate planning, asset protection, and family governance. Our resources are free, accessible, and designed for real families navigating real decisions." },
        { heading:"501(c)(3) Status", body:"AshantiTrust.org is organized as a tax-exempt nonprofit organization under Section 501(c)(3) of the Internal Revenue Code. Donations are tax-deductible to the extent permitted by law. We do not sell legal, tax, or investment advice." },
        { heading:"Disclaimer", body:"All content on this site is for educational purposes only. Nothing here constitutes legal, tax, financial, or investment advice. Users should consult qualified legal counsel, a licensed CPA, and a registered financial advisor before making any planning decisions." },
      ].map((s, i) => (
        <div key={i} style={{ marginBottom:"40px", paddingBottom:"40px", borderBottom:`1px solid ${T.border}` }}>
          <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"26px", color:T.ink, marginBottom:"12px" }}>{s.heading}</h2>
          <p style={{ fontFamily:"EB Garamond, serif", fontSize:"17px", color:T.ink, opacity:0.75, lineHeight:1.7 }}>{s.body}</p>
        </div>
      ))}
    </div>
  );
}

function EventsPage() {
  const EVENTS = [
    { type:"Webinar", title:"Trust Basics 101: Live Q&A", date:"June 24, 2025", time:"7:00 PM ET", desc:"An introduction to revocable and irrevocable trusts for families just beginning estate planning." },
    { type:"Workshop", title:"Trustee Duties Deep Dive", date:"July 10, 2025", time:"1:00 PM ET", desc:"Interactive workshop covering fiduciary investment standards, distribution decisions, and annual compliance." },
    { type:"Trust School", title:"Summer Trust School — 4-Week Program", date:"Aug 4–25, 2025", time:"Weekly · 7:00 PM ET", desc:"Comprehensive four-week course covering trust fundamentals through advanced tax and asset protection concepts." },
    { type:"Summit", title:"Family Governance Summit 2025", date:"September 20, 2025", time:"All Day", desc:"Annual conference on family constitutions, multi-generational wealth transfer, and beneficiary education." },
  ];
  const EVENT_COLORS = { Webinar:T.steel, Workshop:T.gold, "Trust School":T.red, Summit:"#5A4A7A" };
  return (
    <div style={{ maxWidth:"880px", margin:"0 auto", padding:"100px 32px 80px" }}>
      <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:T.steel, marginBottom:"8px" }}>Events</div>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"48px", color:T.ink, marginBottom:"40px" }}>Upcoming Events</h1>
      <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
        {EVENTS.map((ev, i) => (
          <div key={i} style={{
            background:T.surface, border:`1px solid ${T.border}`,
            borderLeft:`4px solid ${EVENT_COLORS[ev.type]}`,
            borderRadius:"3px", padding:"24px 28px",
            display:"flex", gap:"24px", alignItems:"flex-start",
          }}>
            <div style={{ minWidth:"100px" }}>
              <div style={{ fontFamily:"DM Mono, monospace", fontSize:"9px", letterSpacing:"0.12em", textTransform:"uppercase", color:EVENT_COLORS[ev.type], marginBottom:"4px" }}>{ev.type}</div>
              <div style={{ fontFamily:"DM Mono, monospace", fontSize:"11px", color:T.ink, opacity:0.6 }}>{ev.date}</div>
              <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", color:T.ink, opacity:0.45, marginTop:"2px" }}>{ev.time}</div>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"20px", color:T.ink, marginBottom:"8px" }}>{ev.title}</div>
              <div style={{ fontFamily:"EB Garamond, serif", fontSize:"15px", color:T.ink, opacity:0.65, lineHeight:1.55, marginBottom:"14px" }}>{ev.desc}</div>
              <button style={{ height:"32px", padding:"0 18px", background:EVENT_COLORS[ev.type], border:"none", borderRadius:"2px", fontFamily:"DM Mono, monospace", fontSize:"9px", letterSpacing:"0.1em", textTransform:"uppercase", color:"#fff", cursor:"pointer" }}>Register →</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonatePage() {
  return (
    <div style={{ maxWidth:"720px", margin:"0 auto", padding:"100px 32px 80px", textAlign:"center" }}>
      <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.16em", textTransform:"uppercase", color:T.gold, marginBottom:"8px" }}>501(c)(3) Nonprofit</div>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"48px", color:T.ink, marginBottom:"16px" }}>Support Our Mission</h1>
      <p style={{ fontFamily:"EB Garamond, serif", fontSize:"18px", color:T.ink, opacity:0.7, lineHeight:1.6, marginBottom:"48px" }}>
        Your tax-deductible gift funds free trust education, downloadable resources, and financial literacy programs for families across the country.
      </p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:"12px", marginBottom:"24px" }}>
        {["$25","$50","$100","$250","$500","Custom"].map((amt, i) => (
          <button key={i} style={{
            height:"56px", background: i===2 ? T.steel : T.parchment,
            border: i===2 ? "none" : `1px solid ${T.border}`,
            borderRadius:"3px",
            fontFamily:"Bebas Neue, sans-serif", fontSize:"22px",
            color: i===2 ? "#fff" : T.ink, cursor:"pointer",
          }}>{amt}</button>
        ))}
      </div>
      <button style={{
        height:"52px", padding:"0 48px",
        background:T.steel, border:"none", borderRadius:"3px",
        fontFamily:"DM Mono, monospace", fontSize:"12px", letterSpacing:"0.12em",
        textTransform:"uppercase", color:"#fff", cursor:"pointer",
        marginBottom:"20px",
      }}>
        Donate Securely →
      </button>
      <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", color:T.ink, opacity:0.45, lineHeight:1.8 }}>
        AshantiTrust.org is a 501(c)(3) organization.<br />
        Donations are tax-deductible to the extent permitted by law.
      </div>
    </div>
  );
}

/* ─── FOOTER ──────────────────────────────────────────────────────────── */
function Footer({ setPage }) {
  return (
    <footer style={{ background:T.ink, color:"rgba(255,255,255,0.7)", padding:"48px 32px 28px" }}>
      <div style={{ maxWidth:"1100px", margin:"0 auto" }}>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", gap:"40px", marginBottom:"40px" }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"14px" }}>
              <div style={{ width:"28px", height:"28px", background:T.steel, borderRadius:"2px", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <span style={{ fontFamily:"Bebas Neue, sans-serif", fontSize:"14px", color:"#fff" }}>AT</span>
              </div>
              <div style={{ fontFamily:"Cormorant Garamond, serif", fontWeight:700, fontSize:"16px", color:"#fff" }}>AshantiTrust.org</div>
            </div>
            <p style={{ fontFamily:"EB Garamond, serif", fontSize:"14px", lineHeight:1.7 }}>
              Public 501(c)(3) trust education nonprofit. Educational information only — not legal, tax, or financial advice.
            </p>
          </div>
          {[
            { heading:"Learn", links:["Trust Basics","Estate Planning","Tax Planning","Asset Protection","Family Governance"] },
            { heading:"Resources", links:["Checklists","Templates","Tools","Glossary","Events"] },
            { heading:"Organization", links:["About","Mission","Board","Donate","Contact"] },
          ].map((col, i) => (
            <div key={i}>
              <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.12em", textTransform:"uppercase", color:"rgba(255,255,255,0.45)", marginBottom:"12px" }}>{col.heading}</div>
              <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                {col.links.map((l, j) => (
                  <span key={j} style={{ fontFamily:"EB Garamond, serif", fontSize:"14px", cursor:"pointer", color:"rgba(255,255,255,0.65)" }}
                    onClick={() => setPage(col.heading.toLowerCase())}
                  >{l}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{ borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:"20px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"12px" }}>
          <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.08em" }}>
            © 2025 AshantiTrust.org · 501(c)(3) Nonprofit
          </div>
          <div style={{ fontFamily:"DM Mono, monospace", fontSize:"10px", letterSpacing:"0.06em", opacity:0.5 }}>
            family.ashantitrust.org · trustmebro.ashantitrust.org
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── ROOT APP ────────────────────────────────────────────────────────── */
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Bebas+Neue&family=DM+Mono:wght@400;500&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html, body, #root { min-height: 100%; background: #EFECE5; font-family: 'EB Garamond', serif; color: #0A0A0A; -webkit-font-smoothing: antialiased; }
button { transition: opacity 0.15s; }
button:hover { opacity: 0.88; }
input:focus { outline: 2px solid #2B5C8A; outline-offset: 1px; }
`;

export default function AshantiTrust() {
  const [page, setPage]       = useState("home");
  const [adminOpen, setAdmin] = useState(false);
  const contentRef = useRef(null);

  const setPageAndScroll = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <style>{STYLE}</style>
      <Nav activePage={page} setPage={setPageAndScroll} openAdmin={() => setAdmin(true)} />

      {adminOpen && <AdminModal onClose={() => setAdmin(false)} />}

      <main ref={contentRef}>
        {page === "home" && (
          <>
            <Hero setPage={setPageAndScroll} />
            <Disclaimer />
            <TrustTypes setPage={setPageAndScroll} />
            <LearningTracks setPage={setPageAndScroll} />
            <ResourcePreview setPage={setPageAndScroll} />
            <DonateBanner setPage={setPageAndScroll} />
          </>
        )}
        {page === "learn"     && <><LearnPage /><Disclaimer /></>}
        {page === "resources" && <><ResourcesPage /><Disclaimer /></>}
        {page === "about"     && <AboutPage />}
        {page === "events"    && <EventsPage />}
        {page === "donate"    && <DonatePage />}
      </main>

      <Footer setPage={setPageAndScroll} />
    </>
  );
}
